import { useState, useMemo } from 'react'
import { motion, AnimatePresence } from 'framer-motion'
import { Plus, Edit, Trash2, Layers, Check, AlertTriangle } from 'lucide-react'
import { formatCurrency } from '@/utils/format-currency'
import { cn } from '@/utils/cn'
import type { ComboVariation, ComboVariationWithPricing, PropostaItem } from '@/types'
import { calculateAllVariations } from '@/utils/dealCalculations'
import { ComboVariationModal } from './ComboVariationModal'

interface ComboVariationSectionProps {
  variations: ComboVariation[]
  allItems: PropostaItem[]
  onChange: (variations: ComboVariation[]) => void
  onExportPPTX?: (variations: ComboVariationWithPricing[]) => void
  onExportPDF?: (variations: ComboVariationWithPricing[]) => void
}

export function ComboVariationSection({
  variations,
  allItems,
  onChange,
  onExportPPTX,
  onExportPDF,
}: ComboVariationSectionProps) {
  const [modalOpen, setModalOpen] = useState(false)
  const [editingVariation, setEditingVariation] = useState<ComboVariation | null>(null)

  const variationsWithPricing: ComboVariationWithPricing[] = useMemo(
    () => calculateAllVariations(variations, allItems),
    [variations, allItems],
  )

  const activeIds = new Set(allItems.map((i) => i.id))
  const hasStalledIds = variations.some((v) => v.itemIds.some((id) => !activeIds.has(id)))

  const handleOpenCreate = () => {
    setEditingVariation(null)
    setModalOpen(true)
  }

  const handleOpenEdit = (v: ComboVariation) => {
    setEditingVariation(v)
    setModalOpen(true)
  }

  const handleSave = (saved: ComboVariation) => {
    const sanitized = { ...saved, itemIds: saved.itemIds.filter((id) => activeIds.has(id)) }

    if (variations.find((v) => v.id === sanitized.id)) {
      onChange(variations.map((v) => (v.id === sanitized.id ? sanitized : v)))
    } else {
      onChange([...variations, sanitized])
    }
    setModalOpen(false)
  }

  const handleDelete = (id: string) => {
    onChange(variations.filter((v) => v.id !== id))
  }

  const handleSanitize = () => {
    onChange(
      variations.map((v) => ({
        ...v,
        itemIds: v.itemIds.filter((id) => activeIds.has(id)),
      })),
    )
  }

  const canExport = variationsWithPricing.length >= 2

  const matrixItems = allItems.filter((item) =>
    variationsWithPricing.some((v) => v.itemIds.includes(item.id)),
  )

  if (allItems.length === 0) return null

  return (
    <div className="rounded-lg border border-white/[0.06] bg-white/[0.02] px-3 py-2.5 space-y-2">
      <div className="flex items-center justify-between gap-2">
        <div className="flex items-center gap-1.5 min-w-0">
          <Layers size={12} className="text-emerald-600 shrink-0" />
          <span className="text-[10px] font-semibold uppercase tracking-wider text-text-label truncate">
            Opções (plano A vs B)
          </span>
          {variations.length > 0 && (
            <span className="rounded-full bg-white/[0.06] px-1.5 py-0.5 text-[9px] text-text-muted tabular-nums">
              {variations.length}
            </span>
          )}
        </div>
        <button
          type="button"
          onClick={handleOpenCreate}
          className="shrink-0 flex items-center gap-1 rounded-md border border-emerald-500/25 bg-emerald-500/10 px-2 py-1 text-[10px] font-medium text-emerald-600 hover:bg-emerald-500/15 transition-colors"
        >
          <Plus size={10} />
          Criar opção
        </button>
      </div>

      {hasStalledIds && (
        <div className="flex items-center gap-2 rounded-md border border-amber-500/25 bg-amber-500/10 px-2 py-1.5">
          <AlertTriangle size={11} className="shrink-0 text-amber-600" />
          <span className="flex-1 text-[10px] text-amber-800 dark:text-amber-200/90">
            Itens removidos ainda em opções.
          </span>
          <button
            type="button"
            onClick={handleSanitize}
            className="shrink-0 text-[10px] text-amber-700 underline hover:no-underline dark:text-amber-400"
          >
            Limpar
          </button>
        </div>
      )}

      {variations.length > 0 && (
        <>
          <div
            className={cn(
              'gap-2',
              variationsWithPricing.length <= 2
                ? 'grid grid-cols-1 sm:grid-cols-2'
                : 'grid grid-cols-2',
            )}
          >
            <AnimatePresence mode="popLayout">
              {variationsWithPricing.map((v, index) => {
                const isEmpty = v.itemIds.length === 0
                const itemCount = v.itemIds.length
                return (
                  <motion.div
                    key={v.id}
                    layout
                    initial={{ opacity: 0, y: 4 }}
                    animate={{ opacity: 1, y: 0 }}
                    exit={{ opacity: 0, scale: 0.98 }}
                    className={cn(
                      'group relative flex min-w-0 flex-col rounded-lg border p-2 transition-colors',
                      isEmpty
                        ? 'border-red-500/30 bg-red-500/5'
                        : 'border-white/[0.08] bg-white/[0.03] hover:border-white/[0.12] hover:bg-white/[0.05]',
                    )}
                  >
                    <div className="flex items-start justify-between gap-1.5 min-w-0">
                      <div className="min-w-0 flex-1">
                        <span className="text-[9px] font-medium uppercase tracking-wider text-text-muted">
                          Opção {index + 1}
                        </span>
                        <p
                          className="text-[11px] font-semibold text-text-primary leading-tight truncate"
                          title={v.nome}
                        >
                          {v.nome}
                        </p>
                        {!isEmpty && (
                          <p className="text-[9px] text-text-muted mt-0.5">
                            {itemCount} {itemCount === 1 ? 'item' : 'itens'}
                          </p>
                        )}
                      </div>
                      <div className="flex shrink-0 items-center gap-0.5 opacity-80 group-hover:opacity-100">
                        <button
                          type="button"
                          onClick={() => handleOpenEdit(v)}
                          className="rounded p-1.5 text-text-muted hover:bg-white/[0.06] hover:text-text-primary"
                          title="Editar opção"
                          aria-label={`Editar ${v.nome}`}
                        >
                          <Edit size={12} />
                        </button>
                        <button
                          type="button"
                          onClick={() => handleDelete(v.id)}
                          className="rounded p-1.5 text-text-muted hover:bg-red-500/10 hover:text-red-500"
                          title="Remover opção"
                          aria-label={`Remover ${v.nome}`}
                        >
                          <Trash2 size={12} />
                        </button>
                      </div>
                    </div>
                    <p
                      className={cn(
                        'mt-1.5 text-sm font-bold tabular-nums leading-none tracking-tight',
                        isEmpty ? 'text-text-muted' : 'text-accent',
                      )}
                    >
                      {isEmpty ? formatCurrency(0) : `${formatCurrency(v.valorFinal)}/mês`}
                    </p>
                    {!isEmpty && v.valorSetup > 0 && (
                      <p className="text-[9px] text-text-muted tabular-nums mt-0.5">
                        + setup {formatCurrency(v.valorSetup)}
                      </p>
                    )}
                  </motion.div>
                )
              })}
            </AnimatePresence>
          </div>

          {matrixItems.length > 0 && variationsWithPricing.length >= 2 && (
            <div className="overflow-x-auto rounded-md border border-white/[0.06] max-h-36 -mx-0.5 px-0.5">
              <table className="w-full min-w-[280px] text-[10px]">
                <thead>
                  <tr className="border-b border-white/[0.06] bg-white/[0.03]">
                    <th className="sticky left-0 z-[1] min-w-[88px] max-w-[120px] bg-white/[0.03] px-2 py-1.5 text-left font-medium text-text-muted">
                      Item
                    </th>
                    {variationsWithPricing.map((v) => (
                      <th
                        key={v.id}
                        className="min-w-[72px] max-w-[100px] px-2 py-1.5 text-center font-semibold text-text-primary"
                        title={v.nome}
                      >
                        <span className="block truncate">{v.nome}</span>
                        <span className="mt-0.5 block text-[9px] font-normal text-accent tabular-nums">
                          {formatCurrency(v.valorFinal)}
                        </span>
                      </th>
                    ))}
                  </tr>
                </thead>
                <tbody>
                  {matrixItems.map((item) => (
                    <tr key={item.id} className="border-b border-white/[0.04]">
                      <td className="sticky left-0 z-[1] min-w-[88px] max-w-[120px] bg-bg-main/95 px-2 py-1 text-text-secondary truncate backdrop-blur-sm">
                        {item.descricao}
                      </td>
                      {variationsWithPricing.map((v) => (
                        <td key={v.id} className="px-2 py-1 text-center">
                          {v.itemIds.includes(item.id) ? (
                            <Check size={10} className="inline text-emerald-600" />
                          ) : (
                            <span className="text-text-muted">—</span>
                          )}
                        </td>
                      ))}
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}

          {canExport && (onExportPPTX || onExportPDF) && (
            <div className="flex gap-1.5">
              {onExportPPTX && (
                <button
                  type="button"
                  onClick={() => onExportPPTX(variationsWithPricing)}
                  className="flex-1 rounded-md border border-white/[0.06] py-1 text-[10px] font-medium text-text-muted hover:bg-white/[0.04]"
                >
                  PPTX multi-opção
                </button>
              )}
              {onExportPDF && (
                <button
                  type="button"
                  onClick={() => onExportPDF(variationsWithPricing)}
                  className="flex-1 rounded-md border border-white/[0.06] py-1 text-[10px] font-medium text-text-muted hover:bg-white/[0.04]"
                >
                  PDF multi-opção
                </button>
              )}
            </div>
          )}
        </>
      )}

      <ComboVariationModal
        open={modalOpen}
        variation={editingVariation}
        allItems={allItems}
        onSave={handleSave}
        onClose={() => setModalOpen(false)}
      />
    </div>
  )
}
